import { DialogActions, DialogContent, Stack } from '@mui/material';
import { gridFilteredSortedRowIdsSelector, useGridApiRef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import { BulkEditSelectedItems } from '@retail/products/containers';
import { ContainedButton, HelperText, OutlinedButton, TextButton } from '@shared/components';
import { useCallback, useMemo, useState } from 'react';
export interface ItemBatch {
  items: AssortmentItemMinimal[];
  rest: AssortmentItemMinimal[];
}

interface Props {
  items: AssortmentItemMinimal[];
  setItems: (items: AssortmentItemMinimal[]) => void;
  itemsWithoutItemNr: number;
  continueToTags: boolean;
  continueToCustomerAssortments: boolean;
  isSaving: boolean;
  onContinue: (batch: ItemBatch) => void;
  onClose: () => void;
  goBack?: () => void;
}

export function HandleItems({
  items,
  setItems,
  itemsWithoutItemNr,
  continueToTags,
  continueToCustomerAssortments,
  isSaving,
  onContinue,
  onClose,
  goBack,
}: Props) {
  const t = useAppTFunction();
  const apiRef = useGridApiRef();
  const [duplicateRowIds, setDuplicateRowIds] = useState<number[]>([]);
  const [rowCount, setRowCount] = useState<number>(items.length);

  const duplicateItemIds = useMemo(
    () =>
      new Set(
        items
          .filter((item) => duplicateRowIds.includes(item.mgItemNumber))
          .map((item) => item.mgSupplierItemId)
      ),
    [duplicateRowIds, items]
  );

  const getEditedItems = useCallback(
    () =>
      apiRef.current
        ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
        : [],
    [apiRef]
  );

  const removeItem = useCallback(
    (mgSupplierItemId: number) =>
      setItems(getEditedItems().filter((item) => item.mgSupplierItemId !== mgSupplierItemId)),
    [getEditedItems, setItems]
  );

  const continueWithAllItems = useCallback(
    () => onContinue({ items: getEditedItems(), rest: [] }),
    [getEditedItems, onContinue]
  );

  const continueWithFilteredItems = useCallback(() => {
    const filteredRowIds = new Set(gridFilteredSortedRowIdsSelector(apiRef));
    const editedItems = getEditedItems();

    onContinue({
      items: editedItems.filter((item) => filteredRowIds.has(item.mgSupplierItemId)),
      rest: editedItems.filter((item) => !filteredRowIds.has(item.mgSupplierItemId)),
    });
  }, [apiRef, getEditedItems, onContinue]);

  const filteredItemsLabelKey =
    continueToTags || continueToCustomerAssortments
      ? rowCount === 1
        ? 'myAssortment.actions.continueWithFilteredItem'
        : 'myAssortment.actions.continueWithFilteredItems'
      : rowCount === 1
      ? 'products.mgItem.supplierItem.addFilteredItem'
      : 'products.mgItem.supplierItem.addFilteredItems';

  return (
    <>
      <DialogContent
        sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}
        className="padding-0"
      >
        <BulkEditSelectedItems
          apiRef={apiRef}
          selectedItems={items}
          duplicateRowIds={duplicateRowIds}
          rowCount={rowCount}
          setDuplicateRowIds={setDuplicateRowIds}
          setRowCount={setRowCount}
          removeItem={removeItem}
        />
      </DialogContent>
      <DialogActions>
        <Stack direction="row" alignItems="center" spacing={1}>
          {!!itemsWithoutItemNr && (
            <HelperText id="itemsWithoutItemNr" status="error">
              {t('myAssortment.status.itemsRemoved', { count: itemsWithoutItemNr })}
            </HelperText>
          )}
          {duplicateRowIds.length > 0 && (
            <HelperText id="duplicatePrio" status="error">
              {t('products.errors.multipleMainPrio')}
            </HelperText>
          )}
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>

          {goBack && (
            <OutlinedButton color="secondary" size="small" onClick={goBack}>
              {t('common.goBack')}
            </OutlinedButton>
          )}

          {rowCount < items.length && (
            <ContainedButton
              size="small"
              color="secondary"
              onClick={continueWithFilteredItems}
              disabled={gridFilteredSortedRowIdsSelector(apiRef).some((rowId) =>
                duplicateItemIds.has(Number(rowId))
              )}
            >
              {t(filteredItemsLabelKey, { count: rowCount })}
            </ContainedButton>
          )}

          <ContainedButton
            color="secondary"
            loading={isSaving}
            size="small"
            onClick={continueWithAllItems}
            disabled={duplicateRowIds.length > 0}
          >
            {continueToTags
              ? t('myAssortment.actions.continueToTags')
              : continueToCustomerAssortments
              ? t('myAssortment.actions.continueToCustomerAssortments')
              : t('myAssortment.actions.addItems')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </>
  );
}
